import { SettingsGroupsViewProps } from '@/types/views/settingsJobs.types';
import { ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import { ConfirmationDialog, ManageGroupsEvent } from 'klayowebshared';
import { AppContext } from '../../common/AppContext';
import axiosClient from '../../common/AxiosClient';
import { track } from '../../common/track';
import { Button } from '../../components/Button';
import { Paper } from '../../components/Paper';
import { GroupsTable } from '../../components/table/GroupsTable';
import { TextField } from '../../components/TextField';
import { ViewComponent } from '../../components/ViewComponent';
import { SettingsGroup } from '../../data/settings/SettingsGroup';
import { withLocale } from '../../providers/LocaleProvider';
import AddIcon from '../../resources/images/icons-add.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import { trimToLowercase } from '../../utilities';
import { AddEditGroupView } from './AddEditGroupView';

class SettingsGroupsViewBase extends ViewComponent<SettingsGroupsViewProps> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    allowAssignAdminBilling: true,
  };

  constructor(props: any) {
    super(props);

    this.state = {
      ...(ViewComponent as any).state,
      saveGroupError: null,
      search: null,
      actionGroup: null,
      deletingGroup: null,
    };
  }

  componentDidMount() {
    track(ManageGroupsEvent.Viewed);
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    const { onLoadGroups, onLoadGroupMembers } = this.props;
    onLoadGroups?.();
    onLoadGroupMembers?.();
  }

  onViewChange(location: any) {
    if (this.pathEquals('/settings/groups')) this.resetErrors();
  }

  resetErrors() {
    this.setState({ saveGroupError: null });
  }

  onSearch(e: any) {
    this.setState({ search: e.target.value });
  }

  getGroupId() {
    const { location } = this.props;

    const path = location.pathname;
    return path.includes('edit') ? path.replace('/settings/groups/edit/', '') : null;
  }

  getGroup() {
    const { groups } = this.props as any;
    if (!groups) return null;

    const editGroupId = this.getGroupId();
    return (groups as any).get(editGroupId);
  }

  onGroupAction(e: any, group: any) {
    this.setState({ actionTarget: e.target, actionGroup: group });
  }

  onGroupSelect(e: any, group: any) {
    const { history } = this.props;
    track(ManageGroupsEvent.RowClicked);
    history.push('/settings/groups/edit/' + group.groupId);
  }

  onEditGroup() {
    const { history } = this.props;
    const { actionGroup } = this.state;
    this.onCloseActionMenu();

    history.push('/settings/groups/edit/' + actionGroup.groupId);
  }

  onDeleteGroup() {
    const { actionGroup } = this.state;
    this.setState({ deletingGroup: actionGroup });
    this.onCloseActionMenu();
  }

  onCloseActionMenu() {
    this.setState({ actionTarget: null, actionGroup: null });
  }

  onDeleteGroupConfirmed() {
    const { deletingGroup } = this.state;
    this.deleteGroup(deletingGroup);
    this.setState({ deletingGroup: null });
  }

  onCancelDeleteGroup() {
    this.setState({ deletingGroup: null });
  }

  deleteGroup(group: any) {
    const { groups, onLoadGroups } = this.props as any;
    this.context?.setLoading?.('deleting', true);

    axiosClient
      .delete('/Group', {
        data: {
          groupId: group.groupId,
        },
      })
      .then((response: any) => {
        (groups as any).delete(group);
        if (onLoadGroups) onLoadGroups(true);
      })
      .finally(() => {
        this.context?.setLoading?.('deleting', false);
      });
  }

  onSaveGroup(group: any, callback: any) {
    const { user, groups, onLoadGroups, onDataChanged } = this.props as any;

    this.resetErrors();
    this.context?.setLoading?.('saveGroup', true);
    group.jobDefinitionIds = Array.from(new Set(group.jobDefinitionIds));
    if (!group.groupId) {
      axiosClient
        .post('/Group', group.toApiDto())
        .then((response: any) => {
          group.groupId = response.data.group.groupId;
          (groups as any).add(group);
          if (onLoadGroups) onLoadGroups(true);
          if (onDataChanged) onDataChanged('groups');
          (this.props as any).history.push('/settings/groups');
        })
        .finally(() => {
          this.context?.setLoading?.('saveGroup', false);
          callback();
        });
    } else {
      axiosClient
        .put('/Group', group.toApiDto())
        .then((response: any) => {
          (groups as any).update(group);
          if (onLoadGroups) onLoadGroups(true);
          if (onDataChanged) onDataChanged('groups');
          (this.props as any).history.push('/settings/groups');
        })
        .finally(() => {
          this.context?.setLoading?.('saveGroup', false);
        });
    }
  }

  render() {
    const {
      organization,
      theme,
      user,
      location,
      history,
      jobs,
      groups,
      orgSettings,
      onDataChanged,
      onNavigation,
      onBlockNavigation,
      onAllowNavigation,
      onLoadJobs,
      groupMembers,
      translate,
    } = this.props;
    const { search, saveGroupError, actionTarget, actionGroup, deletingGroup } = this.state;

    const path = location.pathname;
    const editMode = path.includes('edit');
    const showAddEditGroupView =
      path.startsWith('/settings/groups/new') || path.startsWith('/settings/groups/edit');

    const editGroup = this.getGroup();
    const editGroupId = this.getGroupId();

    const filterItems = [{ id: 'search', field: 'name', operator: 'contains', value: search }];

    const filterGroups = groups
      ? groups.groups.filter((group: any) => {
          // LearnUpon-linked groups are managed by the sync, so they are not listed here.
          if (group?.isLinkedToLearnUpon) return false;
          if (search) {
            const keyword = trimToLowercase(search || '');
            const groupName = trimToLowercase(group?.name || '');
            return groupName?.includes(keyword);
          } else {
            return true;
          }
        })
      : [];

    return (
      <div>
        {!showAddEditGroupView ? (
          <Paper theme={theme} padding={{ xs: '46px 24px', md: '60px' }} borderFromBreakpoint='md'>
            <h1>{translate('settingsViews.groups.title')}</h1>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              spacing={2}
              sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', padding: '30px 0' }}
            >
              {groups && groups.groups.some((group: any) => !group?.isLinkedToLearnUpon) ? (
                <TextField
                  value={search}
                  dense={true}
                  placeholder={translate('settingsViews.groups.searchGroups')}
                  disabled={user === null}
                  fullWidth={true}
                  autoComplete={false}
                  leadingIcon={<SearchIcon />}
                  onChange={this.onSearch.bind(this)}
                  sx={{ maxWidth: { md: '300px' } }}
                />
              ) : (
                <div></div>
              )}
              <Button
                path={'/settings/groups/new'}
                size='md'
                theme={theme}
                variant='filled'
                showLabelFromBreakpoint='md'
                startIcon={<AddIcon />}
                label={translate('settingsViews.groups.newGroup') as any}
                onClick={() => track(ManageGroupsEvent.NewGroup)}
              />
            </Stack>
            <GroupsTable<SettingsGroup>
              pagination={false}
              showFooter={false}
              rowHasAction={true}
              minHeight='300px'
              theme={theme}
              sortable={true}
              showHeaderFromBreakpoint='md'
              filterItems={filterItems}
              dense={true}
              columns={[
                { type: (GroupsTable as any).columns.name, label: translate('tableColumns.name') },
                {
                  type: (GroupsTable as any).columns.memberCount,
                  label: translate('tableColumns.memberCount'),
                },
                {
                  type: (GroupsTable as any).columns.managerCount,
                  label: translate('tableColumns.managerCount'),
                },
                {
                  type: (GroupsTable as any).columns.actions,
                  label: translate('tableColumns.actions'),
                },
              ]}
              sortModel={[
                {
                  field: 'name',
                  sort: 'asc',
                },
              ]}
              onGroupSelect={this.onGroupSelect.bind(this) as any}
              onGroupAction={this.onGroupAction.bind(this) as any}
              paper={false}
              rows={filterGroups}
              keyword={search}
              noRowsMessage={translate('common.clickNewGroup')}
            />

            {actionGroup && (
              <Menu
                open={actionTarget !== null}
                anchorEl={actionTarget}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                onClose={this.onCloseActionMenu.bind(this)}
              >
                <MenuItem
                  onClick={() => {
                    track(ManageGroupsEvent.MenuOption, { option: 'edit' });
                    this.onEditGroup();
                  }}
                >
                  <ListItemText>{translate('settingsViews.groups.editGroup')}</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    track(ManageGroupsEvent.MenuOption, { option: 'delete' });
                    this.onDeleteGroup();
                  }}
                >
                  <ListItemText>{translate('settingsViews.groups.deleteGroup')}</ListItemText>
                </MenuItem>
              </Menu>
            )}

            {deletingGroup && (
              <ConfirmationDialog
                theme={theme}
                title={translate('settingsViews.groups.deleteGroupTitle')}
                question={
                  deletingGroup.isLinkedToLearnUpon
                    ? translate('settingsViews.groups.deleteGroupLinkedToLearnUpon')
                    : translate('settingsViews.groups.deleteGroupConfirm', {
                        groupName: deletingGroup.name,
                      })
                }
                cancelButton={translate('common.cancel')}
                acceptButton={translate('settingsViews.groups.deleteGroup')}
                acceptDanger={true}
                onCancel={this.onCancelDeleteGroup.bind(this)}
                onAccept={this.onDeleteGroupConfirmed.bind(this)}
                isNotShowAcceptButton={deletingGroup.isLinkedToLearnUpon}
              />
            )}
          </Paper>
        ) : (
          <AddEditGroupView
            {...{
              user,
              organization,
              orgSettings,
              theme,
              editMode,
              group: editMode ? editGroup : new SettingsGroup(),
              groupId: editGroupId,
              error: saveGroupError,
              jobs,
              locations: orgSettings ? orgSettings.locations : null,
              onDataChanged,
              onCancel: (e: any) => history.push('/settings/groups'),
              onSave: this.onSaveGroup.bind(this),
              onLoadJobs,
              onBlockNavigation,
              onAllowNavigation,
              onNavigation,
              groupMembers,
            }}
          />
        )}
      </div>
    );
  }
}

export const SettingsGroupsView = withLocale(SettingsGroupsViewBase);
